
import React, { useState } from 'react';
import { Trash2, Eye, Calendar, Hash } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useAdminGamesDuplicates } from '@/hooks/useAdminGamesDuplicates';

interface DuplicateGame {
  id: string;
  title: string;
  thumbnail: string;
  category: string;
  created_at: string;
  play_count: number;
  fake_play_count: number;
}

interface DuplicateGroup {
  title: string;
  games: DuplicateGame[];
}

const AdminGamesDuplicates: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    duplicateGroups,
    isLoading,
    isDetecting,
    startDetection,
    stopDetection,
    deleteDuplicatesMutation
  } = useAdminGamesDuplicates();

  const [selectedGamesForDeletion, setSelectedGamesForDeletion] = useState<Set<string>>(new Set());

  const handleOpenDialog = () => {
    setDialogOpen(true);
    startDetection();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    stopDetection();
    setSelectedGamesForDeletion(new Set());
  };

  const handleGameSelection = (gameId: string, checked: boolean) => {
    const newSelection = new Set(selectedGamesForDeletion);
    if (checked) {
      newSelection.add(gameId);
    } else {
      newSelection.delete(gameId);
    }
    setSelectedGamesForDeletion(newSelection);
  };

  const handleKeepNewest = (group: DuplicateGroup) => {
    const gamesToDelete = group.games.slice(0, -1); // Keep the last (newest) game
    const newSelection = new Set(selectedGamesForDeletion);
    gamesToDelete.forEach(game => newSelection.add(game.id));
    setSelectedGamesForDeletion(newSelection);
  };

  const handleKeepOldest = (group: DuplicateGroup) => {
    const gamesToDelete = group.games.slice(1); // Keep the first (oldest) game
    const newSelection = new Set(selectedGamesForDeletion);
    gamesToDelete.forEach(game => newSelection.add(game.id));
    setSelectedGamesForDeletion(newSelection);
  };

  const handleDeleteSelected = () => {
    if (selectedGamesForDeletion.size > 0) {
      deleteDuplicatesMutation.mutate(Array.from(selectedGamesForDeletion));
      setSelectedGamesForDeletion(new Set());
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={handleOpenDialog}>
          <Eye className="w-4 h-4 mr-2" />
          Detect Duplicates
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Duplicate Games Detection</DialogTitle>
          <DialogDescription>
            Games with similar titles are grouped together. Select which duplicates to delete.
          </DialogDescription>
        </DialogHeader>
        <AdminGamesDuplicatesContent
          duplicateGroups={duplicateGroups}
          isLoading={isLoading}
          selectedGamesForDeletion={selectedGamesForDeletion}
          onGameSelection={handleGameSelection}
          onKeepNewest={handleKeepNewest}
          onKeepOldest={handleKeepOldest}
          onDeleteSelected={handleDeleteSelected}
          onClose={handleCloseDialog}
          isDeleting={deleteDuplicatesMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  );
};

interface AdminGamesDuplicatesContentProps {
  duplicateGroups: DuplicateGroup[];
  isLoading: boolean;
  selectedGamesForDeletion: Set<string>;
  onGameSelection: (gameId: string, checked: boolean) => void;
  onKeepNewest: (group: DuplicateGroup) => void;
  onKeepOldest: (group: DuplicateGroup) => void;
  onDeleteSelected: () => void;
  onClose: () => void;
  isDeleting: boolean;
}

const AdminGamesDuplicatesContent: React.FC<AdminGamesDuplicatesContentProps> = ({
  duplicateGroups,
  isLoading,
  selectedGamesForDeletion,
  onGameSelection,
  onKeepNewest,
  onKeepOldest,
  onDeleteSelected,
  onClose,
  isDeleting
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Detecting duplicates...</span>
      </div>
    );
  }

  if (duplicateGroups.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Duplicates Found</h3>
        <p className="text-gray-600">All games appear to have unique titles.</p>
        <Button className="mt-4" onClick={onClose}>Close</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Found {duplicateGroups.length} duplicate groups</h3>
          <p className="text-sm text-gray-600">
            {selectedGamesForDeletion.size} games selected for deletion
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="destructive"
            onClick={onDeleteSelected}
            disabled={selectedGamesForDeletion.size === 0 || isDeleting}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {isDeleting ? 'Deleting...' : `Delete Selected (${selectedGamesForDeletion.size})`}
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {duplicateGroups.map((group, groupIndex) => (
          <Card key={groupIndex} className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-lg">{group.title}</h4>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onKeepOldest(group)}
                >
                  Keep Oldest
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onKeepNewest(group)}
                >
                  Keep Newest
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              {group.games.map((game) => (
                <div
                  key={game.id}
                  className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50"
                >
                  <Checkbox
                    checked={selectedGamesForDeletion.has(game.id)}
                    onCheckedChange={(checked) => onGameSelection(game.id, checked === true)}
                  />
                  
                  <img
                    src={game.thumbnail || '/placeholder.svg'}
                    alt={game.title}
                    className="w-16 h-16 object-cover rounded"
                  />
                  
                  <div className="flex-1">
                    <h5 className="font-medium">{game.title}</h5>
                    <p className="text-sm text-gray-600 capitalize">{game.category}</p>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(game.created_at).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Hash className="w-4 h-4" />
                      {game.fake_play_count || game.play_count} plays
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminGamesDuplicates;
